/**
 * Format a score delta for display based on SDUI config.
 *
 * Extracted from ScoreDeltaIndicator to share between web and native.
 * Platform-independent formatting logic — no RN/DOM dependencies.
 *
 * Config options:
 * - displayFormat: 'delta' | 'arrow' | 'percentage' — controls the main format
 * - showSign: boolean — controls +/- prefix (delta/percentage) or arrow glyph (arrow format)
 * - showArrow: boolean — appends a directional arrow suffix to any format
 *
 * Task: #1158.P0.3
 * Agent: Diego <diego@liftping.net>
 */

import type { SDUIScoreDeltaConfig } from '@dramadetector/ui/json-render';

export function formatScoreDelta(
  scoreDelta: number,
  config: SDUIScoreDeltaConfig,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any
): string {
  let formatted: string;

  switch (config.displayFormat) {
    case 'arrow': {
      const arrow = scoreDelta >= 0 ? '\u2191' : '\u2193';
      formatted = `${config.showSign ? arrow : ''}${Math.abs(scoreDelta)}`;
      break;
    }
    case 'percentage':
      formatted = `${config.showSign ? (scoreDelta >= 0 ? '+' : '-') : ''}${Math.abs(scoreDelta * 10)}%`;
      break;
    case 'delta':
    default:
      formatted = t('inline_annotations.score_delta', {
        count: scoreDelta,
        defaultValue: `${scoreDelta >= 0 ? '+' : ''}${scoreDelta} points`,
      });
      break;
  }

  // showArrow appends a directional arrow suffix to any format
  // (independent of displayFormat — e.g. "+3 points ↑" or "+30% ↑")
  if (config.showArrow) {
    const arrowSuffix = scoreDelta >= 0 ? ' \u2191' : ' \u2193';
    formatted += arrowSuffix;
  }

  return formatted;
}
